/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * Modified by TMC. 2023/08
 */

import type { Spread } from 'lexical'

import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical'

import { mention as mentionConst } from '@crew/configs/constants'

/**
 * メンションノードをLexicalの内部表現用JSONで表現する場合の型
 */
export type SerializedMentionNode = Spread<
  {
    mentionName: string
    mentionId: string
    mentionStyleClass: string
    type: typeof mentionConst.CLASS_NAME
    version: 1
  },
  SerializedTextNode
>

/**
 * DOM要素をメンションノードに変換する
 * @param domNode
 * @returns
 */
const convertMentionElement = (
  domNode: HTMLElement
): DOMConversionOutput | null => {
  const mentionName = domNode.textContent
  const mentionId = domNode.dataset[mentionConst.MENTION_ID_DATA_ATTRIBUTE_NAME]
  //get the class name of the mention node ignoring the mention class
  const mentionStyleClass = domNode.className
    .split(' ')
    .filter((className) => className !== mentionConst.CLASS_NAME)
    .join(' ')

  if (!mentionName || !mentionId) {
    // MentionNodeを構成する要素が取得できない場合は何もしない
    return null
  }

  const node = $createMentionNode(mentionName, mentionId, mentionStyleClass)
  return {
    node,
  }
}

/**
 * メンションノード
 */
export class MentionNode extends TextNode {
  __mentionName: string
  __mentionId: string
  __mentionStyleClass: string

  static getType(): string {
    return mentionConst.CLASS_NAME
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.__mentionName,
      node.__mentionId,
      node.__mentionStyleClass,
      node.__text,
      node.__key
    )
  }

  /**
   * Lexicalの内部表現用JSONをメンションノードに変換する
   * @param serializedNode
   * @returns
   */
  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(
      serializedNode.mentionName,
      serializedNode.mentionId,
      serializedNode.mentionStyleClass
    )
    node.setTextContent(serializedNode.text)
    node.setFormat(serializedNode.format)
    node.setDetail(serializedNode.detail)
    node.setMode(serializedNode.mode)
    node.setStyle(serializedNode.style)
    return node
  }

  constructor(
    mentionName: string,
    mentionId: string,
    mentionStyleClass: string,
    text?: string,
    key?: NodeKey
  ) {
    super(text ?? mentionName, key)
    this.__mentionName = mentionName
    this.__mentionId = mentionId
    this.__mentionStyleClass = mentionStyleClass
  }

  /**
   * このメンションノードのLexicalの内部表現用JSONでの表現を取得する
   * @returns
   */
  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionName: this.__mentionName,
      mentionId: this.__mentionId,
      mentionStyleClass: this.__mentionStyleClass,
      type: mentionConst.CLASS_NAME,
      version: 1,
    }
  }

  /**
   * このメンションノードを表示するためのDOM要素を生成する
   * @returns
   */
  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.setAttribute(
      'class',
      `${mentionConst.CLASS_NAME} ${this.__mentionStyleClass}`
    )
    return dom
  }

  /**
   * このメンションノードのvalue用DOM要素での表現を取得する
   * @returns
   */
  exportDOM(): DOMExportOutput {
    const element = document.createElement(mentionConst.TAG_NAME)
    element.setAttribute(
      'class',
      `${mentionConst.CLASS_NAME} ${this.__mentionStyleClass}`
    )
    element.dataset[mentionConst.MENTION_ID_DATA_ATTRIBUTE_NAME] =
      this.__mentionId
    element.textContent = this.__mentionName
    return { element }
  }

  /**
   * DOM表現をメンションノードに変換するためのコンバータを返す
   * @returns
   */
  static importDOM(): DOMConversionMap | null {
    return {
      [mentionConst.TAG_NAME.toLowerCase()]: (domNode: HTMLElement) => {
        // メンション用のカスタムデータがない＝変換対象ではない
        if (
          domNode.dataset[mentionConst.MENTION_ID_DATA_ATTRIBUTE_NAME] ===
          undefined
        ) {
          return null
        }
        return {
          conversion: convertMentionElement,
          priority: 2,
        }
      },
    }
  }

  isSegmented(): false {
    return false
  }

  isTextEntity(): true {
    return true
  }

  isToken(): true {
    return true
  }
}

export function $createMentionNode(
  mentionName: string,
  mentionId: string,
  mentionStyleClass: string
): MentionNode {
  //remove @ from mentionName if exists
  if (mentionName[0] === '@')
    mentionName = mentionName.substring(1, mentionName.length)

  //add @ to mentionName
  mentionName = '@' + mentionName

  const mentionNode = new MentionNode(mentionName, mentionId, mentionStyleClass)
  mentionNode.setMode('segmented').toggleDirectionless()
  return mentionNode
}

export function $isMentionNode(
  node: LexicalNode | null | undefined
): node is MentionNode {
  return node instanceof MentionNode
}
